import { BILLING_RULE_PAYER, ORDER_STATUS } from "@/constants";

const CONFIRMATION_DEPOSIT_REQUIRED_MESSAGE =
  "This order requires a student-paid confirmation deposit. Record a deposit payment on the order before moving to Paid, Confirmed or Fulfilled.";

/**
 * Reads confirmationPayment from order billing_rules.
 */
export const getConfirmationPaymentRule = (billingRules: unknown): string | undefined => {
  if (billingRules === null || typeof billingRules !== "object" || Array.isArray(billingRules)) {
    return;
  }
  const value = (billingRules as Record<string, unknown>).confirmationPayment;
  if (typeof value === "string") {
    return value;
  }
};

export const isInstitutionPaysConfirmationDeposit = (order: { billing_rules?: unknown }): boolean => {
  return getConfirmationPaymentRule(order.billing_rules) === BILLING_RULE_PAYER.INSTITUTION;
};

/**
 * Checks whether an order has a recorded deposit payment by verifying
 * that `deposit_payment_id` exists and is not an empty/whitespace-only string.
 */
export const hasDepositPaymentRecorded = (order: { deposit_payment_id?: string | null }): boolean => {
  return !!order.deposit_payment_id?.trim();
};

export const hasStripeCheckoutSessionRecorded = (order: { stripe_checkout_session_id?: string | null }): boolean => {
  return !!order.stripe_checkout_session_id?.trim();
};

/**
 * Fulfillment: block Paid, Confirmed / Fulfilled when the student pays the confirmation deposit
 * but no deposit_payment_id is stored on the order.
 */
export const validateOrderStatusAllowsPaidConfirmedOrFulfilled = (
  order: {
    billing_rules?: unknown;
    deposit_payment_id?: string | null;
    stripe_checkout_session_id?: string | null;
    confirmation_deposit_in_cents?: number | null;
  },
  nextStatus: string,
): { ok: true } | { ok: false; message: string } => {
  if (nextStatus !== ORDER_STATUS.paidConfirmed && nextStatus !== ORDER_STATUS.fulfilled) {
    return { ok: true };
  }
  if (isInstitutionPaysConfirmationDeposit(order)) {
    return { ok: true };
  }
  // A $0 deposit requires no Stripe payment, so no deposit_payment_id will be present.
  if (order.confirmation_deposit_in_cents === 0) {
    return { ok: true };
  }
  if (hasDepositPaymentRecorded(order)) {
    return { ok: true };
  }
  if (hasStripeCheckoutSessionRecorded(order)) {
    return { ok: true };
  }

  return {
    ok: false,
    message: CONFIRMATION_DEPOSIT_REQUIRED_MESSAGE,
  };
};
